import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { getAuthSafe } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { getHeaderAccountState, SIGNUP_PATHS, type HeaderAccountState } from '../lib/auth/headerAccountState';
import { clearSsoEmtSignup } from '../lib/auth/ssoProfile';
import { readAuthHint } from '../lib/authHint';
import { Button } from './ui/button';

// The pure state rule lives in src/lib/auth/headerAccountState.ts (tested offline).
export { getHeaderAccountState, type HeaderAccountState } from '../lib/auth/headerAccountState';

export function useHeaderAccountState(onSignupStep = false): HeaderAccountState {
  const { user, profile, loading, profileReady } = useAuth();
  return getHeaderAccountState({ user, profile, loading, profileReady, probablySignedIn: readAuthHint(), onSignupStep });
}

/** Sign out from a public header: also drop any half-finished sign-up markers for this tab. */
export function useHeaderSignOut() {
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const run = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      clearSsoEmtSignup();
      try {
        sessionStorage.removeItem('regInProgress');
      } catch {
        // ignore
      }
      await signOut(getAuthSafe());
      navigate('/', { replace: true });
    } catch (err) {
      console.warn('[HeaderAccountActions] Sign out failed:', err);
    } finally {
      setSigningOut(false);
    }
  };
  return { signOut: run, signingOut };
}

/**
 * The account buttons of a public header.
 * - `brand`: the landing page / auth-page bar (rr-btn-secondary + rr-btn-primary).
 * - `shell`: the quieter bar on public job and company pages (outline buttons, "Join Arevina").
 */
export default function HeaderAccountActions({
  variant = 'brand',
  onSignupStep = false,
}: {
  variant?: 'brand' | 'shell';
  /** Rendered by a sign-up step after Register (see getHeaderAccountState). */
  onSignupStep?: boolean;
}) {
  const state = useHeaderAccountState(onSignupStep);
  const { pathname } = useLocation();
  const { signOut: doSignOut, signingOut } = useHeaderSignOut();

  if (state.kind === 'resolving') {
    // Keep the bar's height; never flash "Sign in" at someone who is signed in.
    return <div className="min-h-[2.5rem]" data-testid="header-account-resolving" aria-hidden />;
  }

  if (state.kind === 'signedOut') {
    if (variant === 'shell') {
      return (
        <div className="flex items-center gap-2">
          <Button variant="ghost" asChild size="sm" className="min-h-11">
            <Link to="/signin" data-testid="header-signin">Sign in</Link>
          </Button>
          <Button variant="outline" asChild size="sm" className="min-h-11">
            <Link to="/signup" data-testid="header-create-account">Join Arevina</Link>
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-3">
        <Link to="/signin" className="rr-btn-secondary min-h-[2.5rem]" data-testid="header-signin">
          Sign in
        </Link>
        <Link to="/signup" className="rr-btn-primary min-h-[2.5rem] flex items-center" data-testid="header-create-account">
          Create account
        </Link>
      </div>
    );
  }

  const primary =
    state.kind === 'needsSignup'
      ? onSignupStep || SIGNUP_PATHS.includes(pathname) || pathname === state.finishTo
        ? null
        : { to: state.finishTo, label: 'Finish sign-up', testId: 'header-finish-signup' }
      : { to: state.homeTo, label: variant === 'shell' ? 'Back to my dashboard' : 'Go to dashboard', testId: 'header-go-dashboard' };

  if (variant === 'shell') {
    return (
      <div className="flex items-center gap-2" data-testid="header-account-signed-in" data-state={state.kind}>
        {state.kind === 'needsSignup' && (
          <Button variant="ghost" size="sm" className="min-h-11" onClick={() => void doSignOut()} disabled={signingOut} data-testid="header-signout">
            {signingOut ? 'Signing out…' : 'Sign out'}
          </Button>
        )}
        {primary && (
          <Button variant="outline" asChild size="sm" className="min-h-11">
            <Link to={primary.to} data-testid={primary.testId}>{primary.label}</Link>
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-3" data-testid="header-account-signed-in" data-state={state.kind}>
      <button
        type="button"
        className="rr-btn-secondary min-h-[2.5rem]"
        onClick={() => void doSignOut()}
        disabled={signingOut}
        data-testid="header-signout"
      >
        {signingOut ? 'Signing out…' : 'Sign out'}
      </button>
      {primary && (
        <Link to={primary.to} className="rr-btn-primary min-h-[2.5rem] flex items-center" data-testid={primary.testId}>
          {primary.label}
        </Link>
      )}
    </div>
  );
}
